import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { 
  FiGrid, 
  FiCheckSquare, 
  FiUsers, 
  FiSettings, 
  FiLogOut 
} from 'react-icons/fi';
import { useAuth } from '../../../hooks/useAuth';
import { ROUTES } from '../../../utils/constants';

interface AdminSidebarProps {
  pendingCount?: number;
  isMobile?: boolean;
}

const AdminSidebar: React.FC<AdminSidebarProps> = ({ 
  pendingCount = 0,
  isMobile = false
}) => {
  const location = useLocation();
  const { logout } = useAuth();

  const isActive = (path: string) => {
    return location.pathname.startsWith(path);
  };

  const navItems = [
    { 
      icon: <FiGrid />, 
      label: 'Dashboard', 
      to: ROUTES.ADMIN_DASHBOARD 
    },
    { 
      icon: <FiCheckSquare />, 
      label: 'Meeting Approval', 
      to: ROUTES.ADMIN_MEETINGS,
      badge: pendingCount > 0 ? pendingCount : undefined 
    },
    { 
      icon: <FiUsers />, 
      label: 'User Management', 
      to: ROUTES.ADMIN_USERS 
    },
    { 
      icon: <FiSettings />, 
      label: 'Settings', 
      to: '/admin/settings',
      comingSoon: true
    },
  ];

  return (
    <div className="h-full flex flex-col bg-background-glass/30 backdrop-blur-md border-r border-white/10">
      {/* Logo (Hidden on mobile since it's in the header) */}
      {!isMobile && (
        <div className="py-6 px-4 border-b border-white/10">
          <Link to={ROUTES.ADMIN_DASHBOARD} className="text-2xl font-bold bg-gradient-to-r from-primary-400 to-secondary-400 bg-clip-text text-transparent">
            Admin Panel
          </Link>
        </div>
      )}
      
      {/* Navigation Items */}
      <nav className="flex-1 py-6 px-3 space-y-1 overflow-y-auto">
        {navItems.map((item, index) => (
          <Link 
            key={index}
            to={item.comingSoon ? '#' : item.to} 
            className={`
              flex items-center justify-between py-3 px-4 rounded-lg transition-all duration-300
              ${isActive(item.to) 
                ? 'bg-primary-600/30 text-primary-400 font-medium' 
                : 'text-white/70 hover:bg-white/10 hover:text-white'}
              ${item.comingSoon ? 'opacity-50 cursor-not-allowed' : ''}
            `}
            onClick={e => {
              if (item.comingSoon) {
                e.preventDefault();
              }
            }}
          >
            <div className="flex items-center">
              <span className="text-xl mr-3">{item.icon}</span>
              <span>{item.label}</span>
              {item.comingSoon && (
                <span className="ml-2 text-xs bg-gray-500/30 text-white/70 px-2 py-0.5 rounded-full">
                  Soon
                </span>
              )}
            </div>
            
            {item.badge && (
              <span className="bg-primary-500 text-white text-xs px-2 py-0.5 rounded-full">
                {item.badge}
              </span>
            )}
          </Link>
        ))}
      </nav>
      
      {/* Logout Button */}
      <div className="p-3 border-t border-white/10">
        <button
          onClick={logout}
          className="flex items-center w-full py-3 px-4 rounded-lg text-white/70 hover:bg-white/10 hover:text-white"
        >
          <span className="text-xl mr-3"><FiLogOut /></span>
          <span>Logout</span>
        </button>
      </div>
    </div>
  );
};

export default AdminSidebar;
